import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { motion, AnimatePresence } from 'framer-motion';
import { Routes, Route } from 'react-router-dom';
import BIRDS from 'vanta/dist/vanta.birds.min';
import { animated, useSpring } from '@react-spring/web';
import Tilt from 'react-parallax-tilt';
import {
  CloudArrowUpIcon,
  BoltIcon,
  LockClosedIcon,
  PhotoIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  DocumentDuplicateIcon,
  SparklesIcon,
  ArrowDownTrayIcon,
  ArrowUpIcon,
  ShareIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';

// Import components and services
import Footer from './components/Footer';
import SEO from './components/SEO';
import { removeBackground } from './services/api';
import { useApi } from './hooks/useApi';
import { AuthProvider } from './contexts/AuthContext';
import SignIn from './components/auth/SignIn';
import SignUp from './components/auth/SignUp';
import ForgotPassword from './components/auth/ForgotPassword';
import ProtectedRoute from './components/auth/ProtectedRoute';

// Import pages
import About from './pages/About';
import Contact from './pages/Contact';
import Error from './pages/Error';
import ApiDocs from './pages/ApiDocs';
import Pricing from './pages/Pricing';
import GetStarted from './pages/GetStarted';
import Navigation from './components/Navigation';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Terms from './pages/Terms';
import DMCA from './pages/DMCA';
import Dashboard from './pages/Dashboard';

function AnimatedNumber({ n }) {
  const { number } = useSpring({
    from: { number: 0 },
    to: { number: n },
    delay: 200,
    config: { mass: 1, tension: 20, friction: 10 },
  });

  return <animated.span>{number.to((val) => Math.floor(val))}</animated.span>;
}

const StepCard = ({ icon: Icon, title, description, step }) => (
  <Tilt tiltMaxAngleX={10} tiltMaxAngleY={10} scale={1.05} className="h-full">
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: step * 0.1 }}
      className="glass p-6 md:p-8 rounded-modern text-center hover:bg-white/5 transition-all duration-300 h-full flex flex-col items-center justify-center"
    >
      <div className="relative flex justify-center mb-4 md:mb-6">
        <div className="p-3 md:p-4 bg-gradient-to-br from-[#2563EB] to-[#4F46E5] rounded-full w-12 h-12 md:w-16 md:h-16 flex items-center justify-center glow">
          <Icon className="w-6 h-6 md:w-8 md:h-8 text-white" />
        </div>
        <span className="absolute -top-2 -right-2 w-6 h-6 md:w-8 md:h-8 bg-gradient-to-br from-[#2563EB] to-[#4F46E5] rounded-full text-white text-sm md:text-lg font-bold flex items-center justify-center">
          {step}
        </span>
      </div>
      <h3 className="text-xl md:text-2xl font-semibold mb-3 md:mb-4 text-white text-center">{title}</h3>
      <p className="text-white/80 text-center text-base md:text-lg">{description}</p>
    </motion.div>
  </Tilt>
);

const FeatureCard = ({ icon: Icon, title, description }) => (
  <Tilt tiltMaxAngleX={10} tiltMaxAngleY={10} scale={1.05} className="h-full">
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="glass p-6 md:p-8 rounded-modern text-center hover:bg-white/5 transition-all duration-300 h-full flex flex-col items-center justify-center"
    >
      <div className="flex justify-center mb-4 md:mb-6">
        <div className="p-3 md:p-4 bg-gradient-to-br from-[#2563EB] to-[#4F46E5] rounded-full w-12 h-12 md:w-16 md:h-16 flex items-center justify-center glow">
          <Icon className="w-6 h-6 md:w-8 md:h-8 text-white" />
        </div>
      </div>
      <h3 className="text-xl md:text-2xl font-semibold mb-3 md:mb-4 text-white text-center">{title}</h3>
      <p className="text-white/80 text-center text-base md:text-lg">{description}</p>
    </motion.div>
  </Tilt>
);

function ShareButton({ icon: Icon, label, onClick, className }) {
  return (
    <motion.button
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      onClick={onClick}
      className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-white/80 hover:text-white transition-colors ${className}`}
    >
      <Icon className="h-5 w-5" />
      <span className="text-sm">{label}</span>
    </motion.button>
  );
}

function App() {
  const [vantaEffect, setVantaEffect] = useState(null);
  const vantaRef = useRef(null);
  const [processedImage, setProcessedImage] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [uploadStartTime, setUploadStartTime] = useState(null);
  const { execute: processImage, loading, error, clearError } = useApi(removeBackground);

  // Analytics tracking function
  const trackEvent = useCallback((eventName, eventParams = {}) => {
    if (window.gtag) {
      window.gtag('event', eventName, {
        ...eventParams,
        timestamp: new Date().toISOString()
      });
    }
  }, []);

  const onDrop = useCallback(async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      // Clear previous result and errors immediately when new image is uploaded
      setProcessedImage(null);
      clearError();

      setUploadStartTime(Date.now());
      trackEvent('image_upload_started', {
        file_type: file.type,
        file_size: file.size
      });

      try {
        const result = await processImage(file);
        if (result) {
          setProcessedImage(result);
          trackEvent('image_processing_complete', {
            processing_time: Date.now() - uploadStartTime,
            success: true
          });
        }
      } catch (error) {
        console.error('Failed to process image:', error);
        trackEvent('image_processing_error', {
          error_message: error.message,
          processing_time: Date.now() - uploadStartTime
        });
      }
    }
  }, [processImage, trackEvent, uploadStartTime, clearError]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.webp']
    },
    multiple: false,
    maxSize: 10 * 1024 * 1024 // 10MB
  });

  const handleDownload = useCallback(() => {
    trackEvent('image_download', {
      time_to_download: uploadStartTime ? Date.now() - uploadStartTime : null
    });

    const link = document.createElement('a');
    link.href = processedImage;
    link.download = 'remuvr-processed-image.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [processedImage, uploadStartTime, trackEvent]);

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'My Processed Image from Remuvr',
          text: 'Check out this image I processed with Remuvr!',
          url: window.location.origin
        });
        
        trackEvent('image_share', {
          share_method: 'web_share_api',
          time_to_share: uploadStartTime ? Date.now() - uploadStartTime : null
        });
      } catch (error) {
        console.error('Error sharing:', error);
        trackEvent('share_error', {
          error_message: error.message
        });
      }
    }
  };

  const handleEmail = () => {
    trackEvent('email_instructions_viewed');
    setShowEmailModal(true);
    setTimeout(() => setShowEmailModal(false), 5000);
  };

  // Track page view duration
  useEffect(() => {
    const startTime = Date.now();
    return () => {
      const duration = Date.now() - startTime;
      trackEvent('page_view_duration', {
        duration_seconds: Math.floor(duration / 1000)
      });
    };
  }, [trackEvent]);

  useEffect(() => {
    if (!vantaEffect && vantaRef.current) {
      setVantaEffect(
        BIRDS({
          el: vantaRef.current,
          mouseControls: true,
          touchControls: true,
          gyroControls: false,
          minHeight: 200.00,
          minWidth: 200.00,
          scale: 1.00,
          scaleMobile: 1.00,
          backgroundColor: 0x1e3a8a,
          color1: 0x2563eb,
          color2: 0x4f46e5,
          colorMode: "lerpGradient",
          birdSize: 1.50,
          wingSpan: 24.00,
          speedLimit: 5.00,
          separation: 33.00,
          alignment: 44.00,
          cohesion: 44.00,
          quantity: 3.00
        })
      );
    }
    return () => {
      if (vantaEffect) vantaEffect.destroy();
    };
  }, [vantaEffect]);

  // Clean up function for privacy
  const cleanupImage = useCallback(() => {
    if (processedImage) {
      URL.revokeObjectURL(processedImage);
      setProcessedImage(null);
    }
  }, [processedImage]);

  // Cleanup on unmount or when navigating away
  useEffect(() => {
    window.addEventListener('beforeunload', cleanupImage);
    return () => {
      window.removeEventListener('beforeunload', cleanupImage);
      cleanupImage();
    };
  }, [cleanupImage]);

  return (
    <AuthProvider>
      <div className="min-h-screen bg-gradient-to-b from-[#1E40AF] to-[#1E3A8A]">
        <SEO 
          title="Remuvr - AI Background Removal | Remove Background from Images Instantly"
          description="Transform your images instantly with Remuvr's AI background removal tool. Perfect for e-commerce, product photography, and design work. Free to try, no sign-up required. Remove backgrounds in seconds with 100% accuracy."
          type="website"
          pageName="home"
          url="https://remuvr.com"
        />
        <Navigation />
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50"
            >
              <div className="bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg">
                {error}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        <main>
          <Routes>
            <Route path="/" element={
              <div className="min-h-screen text-white p-8">
                {/* Hero Section */}
                <section className="relative min-h-screen flex items-center" ref={vantaRef}>
                  <div className="container mx-auto px-4">
                    <div className="max-w-4xl mx-auto text-center mb-16">
                      <motion.h1 
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="text-4xl md:text-5xl lg:text-7xl font-extrabold mb-6 md:mb-8 text-white transition-transform duration-300 hover:scale-105"
                      >
                        Remove Image Backgrounds
                        <span className="block text-[#22C55E]">in Seconds</span>
                      </motion.h1>
                      <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        className="text-xl md:text-2xl mb-8 md:mb-12 text-white/90 transition-opacity duration-300 hover:opacity-80 px-4"
                        role="doc-subtitle"
                      >
                        Instantly remove backgrounds from images with our AI-powered tool
                      </motion.p>
                    </div>
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.5, delay: 0.4 }}
                      className="px-4 md:px-0"
                    >
                      <div
                        {...getRootProps()}
                        className={`p-6 md:p-12 border-3 border-dashed rounded-modern glass text-center cursor-pointer transition-all duration-300 transform hover:scale-105
                          ${isDragActive ? 'border-white bg-white/20' : 'border-white/50 hover:border-white'}`}
                      >
                        <input {...getInputProps()} />
                        {loading ? (
                          <div className="flex flex-col items-center">
                            <ArrowPathIcon className="mx-auto h-16 w-16 text-white animate-spin" />
                            <p className="mt-6 text-xl text-white">Processing your image...</p>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center">
                            <PhotoIcon className="mx-auto h-16 w-16 text-white" />
                            <p className="mt-6 text-xl text-white">
                              Drop your image or click to upload
                            </p>
                            <p className="mt-2 text-sm text-white/70">
                              Supported formats: JPEG, PNG, WebP (max 10MB)
                            </p>
                          </div>
                        )}
                      </div>
                    </motion.div>
                    
                    {/* Display processed image */}
                    {processedImage && (
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="w-full max-w-4xl mx-auto mt-8 p-6 rounded-xl bg-white/5 backdrop-blur-sm"
                      >
                        <div className="flex justify-center">
                          <img
                            src={processedImage}
                            alt="Processed"
                            className="w-full max-w-3xl h-auto rounded-lg shadow-xl mb-6"
                          />
                        </div>
                        <div className="flex flex-col items-center space-y-4 max-w-md mx-auto">
                          {/* Primary Download Button */}
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={handleDownload}
                            className="w-full bg-gradient-to-r from-[#22C55E] to-[#16A34A] text-white font-semibold py-3 px-8 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center space-x-2"
                          >
                            <ArrowDownTrayIcon className="h-5 w-5" />
                            <span>Download Image</span>
                          </motion.button>

                          {/* Share Options */}
                          <div className="flex items-center justify-center space-x-3 w-full opacity-80 hover:opacity-100 transition-opacity">
                            <ShareButton
                              icon={ShareIcon}
                              label="Share"
                              onClick={handleShare}
                              className="bg-[#4F46E5]/20 hover:bg-[#4F46E5]/30"
                            />
                            <div className="relative">
                              <ShareButton
                                icon={EnvelopeIcon}
                                label="Email"
                                onClick={handleEmail}
                                className="bg-[#4F46E5]/20 hover:bg-[#4F46E5]/30"
                              />
                              {/* Email Modal */}
                              {showEmailModal && (
                                <motion.div
                                  initial={{ opacity: 0, y: 10 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  exit={{ opacity: 0 }}
                                  className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 w-64 p-3 bg-white/10 backdrop-blur-md rounded-lg shadow-xl text-sm text-white/90"
                                >
                                  <p>To email your image:</p>
                                  <ol className="list-decimal ml-4 mt-1 space-y-1">
                                    <li>Click the Download button above</li>
                                    <li>Create a new email</li>
                                    <li>Attach the downloaded image</li>
                                  </ol>
                                  <div className="absolute left-1/2 -bottom-2 -translate-x-1/2 w-4 h-4 rotate-45 bg-white/10"></div>
                                </motion.div>
                              )}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </div>
                </section>

                {/* How It Works */}
                <section className="py-32">
                  <div className="container mx-auto px-4">
                    <h2 className="text-6xl font-bold text-center mb-20 text-white" id="how-it-works">
                      How It Works
                    </h2>
                    <div className="grid md:grid-cols-3 gap-12 max-w-6xl mx-auto">
                      <StepCard
                        icon={CloudArrowUpIcon}
                        title="Upload Image"
                        description="Drop your image or click to upload"
                        step={1}
                      />
                      <StepCard
                        icon={SparklesIcon}
                        title="Auto Processing"
                        description="Our AI removes the background instantly"
                        step={2}
                      />
                      <StepCard
                        icon={CheckCircleIcon}
                        title="Download Result"
                        description="Get your image with background removed"
                        step={3}
                      />
                    </div>
                  </div>
                </section>

                {/* Features */}
                <section className="py-32" id="features">
                  <div className="container mx-auto px-4">
                    <h2 className="text-6xl font-bold text-center mb-8 bg-gradient-to-br from-white to-white/60 bg-clip-text text-transparent" id="features">
                      Why Choose Remuvr
                    </h2>
                    <p className="text-xl text-white/80 text-center mb-20 max-w-3xl mx-auto">
                      Experience the best in AI-powered background removal with features designed for professionals and businesses.
                    </p>
                    <div className="grid md:grid-cols-3 gap-12 max-w-6xl mx-auto">
                      <FeatureCard
                        icon={BoltIcon}
                        title="Lightning Fast"
                        description="Process images in seconds with our advanced AI technology"
                      />
                      <FeatureCard
                        icon={LockClosedIcon}
                        title="100% Private"
                        description="Your images are automatically deleted after processing"
                      />
                      <FeatureCard
                        icon={DocumentDuplicateIcon}
                        title="Bulk Processing"
                        description="Remove backgrounds from multiple images at once"
                      />
                    </div>
                  </div>
                </section>

                {/* Trust Signals */}
                <section className="py-32 glass-dark">
                  <div className="container mx-auto">
                    <div className="max-w-6xl mx-auto px-4">
                      <div className="grid md:grid-cols-4 gap-12 text-center">
                        <div className="glass p-8 rounded-modern">
                          <h3 className="text-3xl lg:text-4xl font-bold text-white" aria-label="Total Images Processed">
                            <AnimatedNumber n={1000000} />+
                          </h3>
                          <p className="mt-2 text-white/80">Images Processed</p>
                        </div>
                        <div className="glass p-8 rounded-modern">
                          <h3 className="text-3xl lg:text-4xl font-bold text-white" aria-label="User Rating">
                            4.9/5
                          </h3>
                          <p className="mt-2 text-white/80">User Rating</p>
                        </div>
                        <div className="glass p-8 rounded-modern">
                          <h3 className="text-3xl lg:text-4xl font-bold text-white" aria-label="Global Usage">
                            Global
                          </h3>
                          <p className="mt-2 text-white/80">Used by professionals</p>
                        </div>
                        <div className="glass p-8 rounded-modern">
                          <h3 className="text-3xl lg:text-4xl font-bold text-white" aria-label="Pricing">
                            Free
                          </h3>
                          <p className="mt-2 text-white/80">No watermarks</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Final CTA */}
                <section className="bg-gradient-to-r from-[#2563EB] to-[#4F46E5] glass-dark">
                  <div className="container mx-auto px-4 py-16 md:py-24">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8 }}
                      className="max-w-3xl mx-auto text-center"
                    >
                      <h2 className="text-3xl md:text-5xl font-bold text-white mb-8">
                        Ready to Remove Backgrounds?
                      </h2>
                      <button
                        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                        className="inline-flex items-center px-8 py-4 bg-white text-[#2563EB] text-lg font-semibold rounded-full hover:bg-white/90 transition-colors duration-300"
                      >
                        Try It Now
                        <ArrowUpIcon className="w-5 h-5 ml-2" />
                      </button>
                    </motion.div>
                  </div>
                </section>

              </div>
            } />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/api-docs" element={<ApiDocs />} />
            <Route path="/get-started" element={<GetStarted />} />
            <Route path="/signin" element={<SignIn />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/reset-password" element={<ForgotPassword />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/dmca" element={<DMCA />} />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } />
            <Route path="*" element={<Error />} />
          </Routes>
        </main>
        <Footer />
      </div>
    </AuthProvider>
  );
}

export default App;
